import axios from "axios";
import {
  ApiHistoryRow,
  ApiReport,
  DepositTransaction,
  DrilldownTarget,
  ExpenseTransaction,
  IncomeTransaction,
  MonthSettingsInput,
  PeriodSelection,
} from "./types";

const API_URL = process.env.REACT_APP_API_URL;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

// The backend wraps every payload as { code, status, message, data }.
const unwrap = <T,>(res: { data: { data: T } }): T => res.data.data;

const periodParams = (period: PeriodSelection) =>
  period.mode === "month"
    ? { year: period.year, month: period.month }
    : { start_date: period.startDate || undefined, end_date: period.endDate || undefined };

export const fetchReport = async (projectId: string, period: PeriodSelection): Promise<ApiReport> => {
  const url =
    period.mode === "month"
      ? `${API_URL}/project/${projectId}/monthly-financial-report`
      : `${API_URL}/project/${projectId}/balance-sheet`;
  return unwrap(await axios.get(url, { params: periodParams(period), headers: authHeaders() }));
};

export const fetchHistory = async (projectId: string, year: number): Promise<ApiHistoryRow[]> =>
  unwrap(
    await axios.get(`${API_URL}/project/${projectId}/monthly-financial-report/history`, {
      params: { year },
      headers: authHeaders(),
    })
  );

const monthUrl = (projectId: string, year: number, month: number) =>
  `${API_URL}/project/${projectId}/monthly-financial-report/${year}/${month}`;

/** Each returns the refreshed monthly report. */
export const saveMonthSettings = async (
  projectId: string,
  year: number,
  month: number,
  settings: MonthSettingsInput
): Promise<ApiReport> => unwrap(await axios.put(monthUrl(projectId, year, month), settings, { headers: authHeaders() }));

export const closeMonth = async (projectId: string, year: number, month: number): Promise<ApiReport> =>
  unwrap(await axios.post(`${monthUrl(projectId, year, month)}/close`, {}, { headers: authHeaders() }));

export const reopenMonth = async (projectId: string, year: number, month: number): Promise<ApiReport> =>
  unwrap(await axios.post(`${monthUrl(projectId, year, month)}/reopen`, { confirm: true }, { headers: authHeaders() }));

/** Best human-readable message out of a failed request (422 lists, 409/403 messages). */
export const apiErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })?.response?.data;
  const firstErrors = data?.errors ? Object.values(data.errors).flat() : [];
  return firstErrors.length ? firstErrors.join(" ") : data?.message || fallback;
};

export type DrilldownRows = ExpenseTransaction[] | IncomeTransaction[] | DepositTransaction[];

export const fetchTransactions = async (
  projectId: string,
  period: PeriodSelection,
  target: DrilldownTarget
): Promise<DrilldownRows> =>
  unwrap(
    await axios.get(`${API_URL}/project/${projectId}/monthly-financial-report/transactions`, {
      params: {
        kind: target.kind,
        group_id: target.id,
        ...(period.mode === "range" ? { range: 1 } : {}),
        ...periodParams(period),
      },
      headers: authHeaders(),
    })
  );
